// Fixture data for the AI-modification MOCK client, standing in for the
// gateway's answers when REACT_APP_MODIFICATION_MODE=mock. These values are the
// server's, not the browser's: the screens never read this file. The labels,
// the limit, the categories and the file limits mirror the seed script's DRAFT
// values (CodeWord-Back-NEW ops/scripts/seed-modification-config.ts), so the
// mock looks like dev will. Out of scope for the no-hard-coding test on purpose.
import type { ModificationTerms } from '../../models/modification.interface';
import { MOCK_AGREEMENT_RENDER } from './fixtures.agreement';

export const MOCK_CONFIG = Object.freeze({
  rejectionLimit: 3,
  staleReviewDays: 7,
  // The gateway's link window (Igor's review of #18): at most this many upload
  // links per revision in any rolling hour, refused with the gateway's own
  // sentence (modification-error.constant.ts, MODIFICATION_SLOT_LIMIT).
  maxSlotsPerRevision: 3,
  slotWindowSeconds: 60 * 60,
  slotLimitMessage: 'No more upload links can be issued just now. Please try again later',
  statusLabels: Object.freeze({
    awaiting_upload: 'Awaiting upload',
    verifying: 'Checking file',
    pending: 'Pending review',
    changes_requested: 'With comments',
    rejected: 'Rejected',
    locked: 'Rejected, locked',
    accepted: 'Accepted',
  }) as Readonly<Record<string, string>>,
});

export const MOCK_TERMS: ModificationTerms = {
  templateId: 'modification-terms',
  version: 1,
  sha256: '6d6f636b2d7465726d732d76312d6e6f742d612d7265616c2d686173682d2d21',
  body: [
    'DRAFT (mock data, not legal text). The real modification terms are served by the gateway.',
    '',
    'This placeholder stands where the real terms will appear, with their line breaks kept.',
  ].join('\n'),
  limits: {
    allowedContentTypes: ['video/mp4', 'video/quicktime'],
    // The seed's value since the file check streams (gateway 701fd80): 10 GiB.
    maxBytes: 10 * 1024 * 1024 * 1024,
    textMaxChars: 500,
    commentMaxChars: 2000,
    categories: ['cleanup', 'restoration', 'reframing', 'background_treatment', 'subtitling', 'translation', 'other'],
  },
};

/** E1 once the addendum is on, as dev answers after the seed: no old terms, the five types, the notice, the limits. */
export const MOCK_TERMS_WITH_AGREEMENT: ModificationTerms = {
  templateId: null,
  version: null,
  sha256: null,
  body: null,
  limits: { ...MOCK_TERMS.limits, categories: MOCK_AGREEMENT_RENDER.modificationTypes.map((type) => type.id) },
  modificationTypes: MOCK_AGREEMENT_RENDER.modificationTypes,
  modificationNotice: MOCK_AGREEMENT_RENDER.modificationNotice,
  agreement: {
    ...MOCK_AGREEMENT_RENDER.agreementTemplate,
    ...MOCK_AGREEMENT_RENDER.limits,
    fieldLabels: MOCK_AGREEMENT_RENDER.fieldLabels,
    checkboxText: MOCK_AGREEMENT_RENDER.preview.checkboxText,
    buttonLabel: MOCK_AGREEMENT_RENDER.preview.buttonLabel,
  },
};

export const MOCK_TENANT = 'mock-licensor';
/** The mock owner's name, as the server's config would give it for MOCK_TENANT (A6). */
export const MOCK_OWNER_NAME = 'Mock Licensor';
export const MOCK_CUSTOMER = 'mock-buyer';
export const MOCK_VIDEO_ID = '311624281fb846699e41e04859236db9';

export const MOCK_ORDER_IDS = Object.freeze({
  eligible: 'a1b2c3d4-0000-4000-8000-0000000000e1',
  withModification: 'a1b2c3d4-0000-4000-8000-0000000000e7',
});

/** One seeded record per state, as the mock's starting point. */
export interface MockSeed {
  key: string;
  status: string;
  title: string;
  orderId: string;
  revisions: number;
  rejections: number;
  pendingDaysAgo?: number;
  lastFailureReason?: string | null;
  history: Array<{ type: string; actor: 'buyer' | 'approver' | 'admin' | 'system'; comment?: string; reason?: string; to: string }>;
}

const order = (n: number) => `a1b2c3d4-0000-4000-8000-0000000000${String(n).padStart(2, '0')}`;

export const MOCK_SEEDS: MockSeed[] = [
  {
    key: 'awaiting',
    status: 'awaiting_upload',
    title: 'Red carpet arrival',
    orderId: order(11),
    revisions: 1,
    rejections: 0,
    lastFailureReason: 'not_a_video',
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'slot_issued', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_failed', actor: 'system', reason: 'not_a_video', to: 'awaiting_upload' },
    ],
  },
  {
    key: 'verifying',
    status: 'verifying',
    title: 'Studio interview, take 2',
    orderId: order(12),
    revisions: 1,
    rejections: 0,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'slot_issued', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
    ],
  },
  {
    key: 'pending',
    status: 'pending',
    title: 'Premiere crowd shot',
    orderId: order(13),
    revisions: 1,
    rejections: 0,
    pendingDaysAgo: 1,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'slot_issued', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
    ],
  },
  {
    key: 'stale',
    status: 'pending',
    title: 'Backstage rehearsal',
    orderId: order(14),
    revisions: 2,
    rejections: 1,
    pendingDaysAgo: 12,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
      { type: 'rejected', actor: 'approver', comment: 'The logo is still visible in the corner.', to: 'rejected' },
      { type: 'slot_issued', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
    ],
  },
  {
    key: 'changes',
    status: 'changes_requested',
    title: 'Acceptance speech',
    orderId: order(15),
    revisions: 1,
    rejections: 0,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
      { type: 'changes_requested', actor: 'approver', comment: 'Please keep the original colour grade in the first two seconds.', to: 'changes_requested' },
    ],
  },
  {
    key: 'rejected',
    status: 'rejected',
    title: 'Press line, wide',
    orderId: order(16),
    revisions: 2,
    rejections: 2,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
      { type: 'rejected', actor: 'approver', comment: 'The face replacement is not allowed.', to: 'rejected' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
      { type: 'rejected', actor: 'approver', comment: 'Still altered. Please remove the face change.', to: 'rejected' },
    ],
  },
  {
    key: 'locked',
    status: 'locked',
    title: 'Concert encore',
    orderId: order(17),
    revisions: 3,
    rejections: 3,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'rejected', actor: 'approver', comment: 'Not approved.', to: 'rejected' },
      { type: 'rejected', actor: 'approver', comment: 'Not approved, same issue.', to: 'rejected' },
      { type: 'rejected', actor: 'approver', comment: 'Third refusal: the lyrics were changed.', to: 'locked' },
    ],
  },
  {
    key: 'accepted',
    status: 'accepted',
    title: 'Behind the scenes',
    orderId: MOCK_ORDER_IDS.withModification,
    revisions: 1,
    rejections: 0,
    history: [
      { type: 'created', actor: 'buyer', to: 'awaiting_upload' },
      { type: 'submitted', actor: 'buyer', to: 'verifying' },
      { type: 'verification_passed', actor: 'system', to: 'pending' },
      { type: 'accepted', actor: 'approver', comment: 'Approved. Thank you.', to: 'accepted' },
    ],
  },
];
